/**
 * EL TELÉFONO COMO ESCÁNER DE ESTA COMPUTADORA (2026-11-02).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE PIDIÓ EL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «En todos los lugares donde se pueda escanear en PC, en vez del icono de
 * cámara un icono de teléfono, y que se pueda conectar con un QR […] que sea
 * independiente por dispositivo: si yo escaneo el QR de mi PC, solo en mi PC,
 * no en mi laptop.»
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO FUNCIONA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Esta computadora guarda en SU navegador el id de su enlace (`ENLACE`). Por
 * eso es por aparato: la portátil, con la misma cuenta, no tiene ese id.
 *
 * Mientras Softfi está abierto y hay enlace, se le pregunta al servidor sin
 * parar, con una pregunta que se queda esperando hasta 20 segundos y contesta
 * en cuanto el teléfono manda un código (ver `modules/telefono/router.py`).
 * Así el código aparece al instante sin preguntar cinco veces por segundo.
 *
 * Lo que llega va, en este orden:
 *
 *   1. al escáner del teléfono que esté abierto (se tocó el teléfono en una
 *      casilla: `capturarDelTelefono`);
 *   2. si no, igual que un lector láser: a la pantalla que pide códigos (la
 *      Caja, el Catálogo, una Entrada…) o a la consulta rápida. Ver
 *      `lectorGlobal.ts`. Con el teléfono unido, en la Caja ni hace falta
 *      tocar nada: se escanea y entra al carrito.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ CON DOS PESTAÑAS DE SOFTFI, SOLO ESCUCHA UNA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Las dos pestañas tienen el mismo enlace (es el mismo navegador). Si las dos
 * escucharan, se repartirían los códigos al azar: la mitad de la compra en
 * una venta y la mitad en la otra. Escucha la última que se usó (`PESTANA`).
 */

import { useEffect, useRef } from "react";
import { create } from "zustand";

import { api, SoftfiError } from "@/lib/api";
import { aviso } from "@/stores/ui";

const ENLACE = "softfi.telefono.enlace";
const PESTANA = "softfi.telefono.pestana";

type Recogida = {
  telefono_conectado: boolean;
  telefono: string | null;
  lecturas: { id: number; codigo: string }[];
};

type EstadoDelTelefono = {
  enlaceId: string | null;
  conectado: boolean;
  telefono: string | null;
  /** El QR está a la vista: se mira más a menudo si ya llegó el teléfono. */
  atento: boolean;
};

function leer(clave: string): string | null {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: string | null) {
  try {
    if (valor === null) localStorage.removeItem(clave);
    else localStorage.setItem(clave, valor);
  } catch {
    /* Sin almacenamiento (ventana privada) el enlace dura lo que la pestaña. */
  }
}

export const useTelefono = create<EstadoDelTelefono>(() => ({
  enlaceId: leer(ENLACE),
  conectado: false,
  telefono: null,
  atento: false,
}));

function olvidarEnlace() {
  escribir(ENLACE, null);
  useTelefono.setState({ enlaceId: null, conectado: false, telefono: null });
}

// ── Pedir el QR y soltar el teléfono ────────────────────────────────────────

/** Cómo se llama esta computadora, para que el teléfono diga a cuál está unido. */
export function nombreDelEquipo(ua = navigator.userAgent): string {
  const sistema = /Windows/i.test(ua)
    ? "Windows"
    : /CrOS/i.test(ua)
      ? "Chromebook"
      : /Mac OS X|Macintosh/i.test(ua)
        ? "Mac"
        : /Linux/i.test(ua)
          ? "Linux"
          : "Computadora";
  const navegador = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : "";
  return navegador ? `${sistema} · ${navegador}` : sistema;
}

export type Qr = { enlace_id: string; codigo: string; vence: string };

/** Un código nuevo para el QR de esta computadora. */
export async function pedirQr(): Promise<Qr> {
  const qr = await api<Qr>("/tenant/telefono/qr", {
    method: "POST",
    body: { enlace_id: useTelefono.getState().enlaceId, equipo: nombreDelEquipo() },
  });
  escribir(ENLACE, qr.enlace_id);
  useTelefono.setState({ enlaceId: qr.enlace_id });
  return qr;
}

/**
 * La dirección que lleva el QR.
 *
 * ⚠️ El código va detrás de `#` y no como `?codigo=`: lo que va detrás de `#`
 * no sale del teléfono, así que no se queda en el registro del servidor ni en
 * el de nadie por el camino.
 */
export function direccionDelQr(codigo: string): string {
  return `${window.location.origin}/telefono#${codigo}`;
}

export async function soltarTelefono(): Promise<void> {
  const { enlaceId } = useTelefono.getState();
  if (!enlaceId) return;
  try {
    await api(`/tenant/telefono/${enlaceId}`, { method: "DELETE" });
  } catch (e) {
    // Ya no existía: da lo mismo, el resultado es el que se buscaba.
    if (!(e instanceof SoftfiError && e.status === 404)) throw e;
  }
  olvidarEnlace();
}

// ── Quién recibe lo que llega ───────────────────────────────────────────────

const capturas: ((codigo: string) => void)[] = [];

/**
 * Mientras el escáner del teléfono de una casilla esté abierto, lo que llegue
 * es suyo, antes que de la pantalla. Devuelve con qué soltarlo.
 */
export function capturarDelTelefono(fn: (codigo: string) => void): () => void {
  capturas.push(fn);
  return () => {
    const i = capturas.lastIndexOf(fn);
    if (i >= 0) capturas.splice(i, 1);
  };
}

// ── La escucha ──────────────────────────────────────────────────────────────

function dormir(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((listo) => {
    const t = window.setTimeout(listo, ms);
    signal?.addEventListener("abort", () => {
      window.clearTimeout(t);
      listo();
    });
  });
}

/**
 * Escucha al teléfono de esta computadora. Va una sola vez, en `LectorGlobal`.
 *
 * `alLeer` es lo que haría un lector láser con el código; devuelve `false` si
 * nadie lo recibió.
 */
export function useEscucharAlTelefono(alLeer: (codigo: string) => boolean) {
  const alLeerRef = useRef(alLeer);
  alLeerRef.current = alLeer;

  useEffect(() => {
    let vivo = true;
    let control = new AbortController();
    const yo = crypto.randomUUID?.() ?? String(Math.random());

    const marcar = () => {
      if (document.visibilityState === "visible") escribir(PESTANA, yo);
    };
    marcar();
    window.addEventListener("focus", marcar);
    document.addEventListener("visibilitychange", marcar);
    // Otra pestaña pasó a ser la que escucha: la pregunta de esta se corta ya,
    // o se llevaría el siguiente código.
    const alCambiarDePestana = (e: StorageEvent) => {
      if (e.key === PESTANA && e.newValue !== yo) control.abort();
    };
    window.addEventListener("storage", alCambiarDePestana);

    // Si cambia el enlace o se abre el QR, la pregunta en curso se corta y se
    // hace otra con lo nuevo: no se esperan 20 segundos con lo de antes.
    const soltar = useTelefono.subscribe((ahora, antes) => {
      if (ahora.enlaceId !== antes.enlaceId || ahora.atento !== antes.atento) control.abort();
    });

    const entregarLecturas = (lecturas: Recogida["lecturas"]) => {
      for (const { codigo } of lecturas) {
        const captura = capturas.at(-1);
        if (captura) {
          captura(codigo);
          continue;
        }
        if (!alLeerRef.current(codigo)) {
          aviso.info(
            `Código recibido del teléfono: ${codigo}`,
            "En esta ventana, selecciona el ícono del teléfono junto al campo correspondiente.",
          );
        }
      }
    };

    void (async () => {
      while (vivo) {
        const { enlaceId, conectado, atento } = useTelefono.getState();
        if (!enlaceId || leer(PESTANA) !== yo) {
          await dormir(1000);
          continue;
        }
        control = new AbortController();
        try {
          const r = await api<Recogida>(`/tenant/telefono/${enlaceId}/lecturas`, {
            params: { conectado, atento, espera: 20 },
            signal: control.signal,
          });
          if (!vivo || useTelefono.getState().enlaceId !== enlaceId) continue;
          useTelefono.setState({ conectado: r.telefono_conectado, telefono: r.telefono });
          entregarLecturas(r.lecturas);
        } catch (e) {
          if (control.signal.aborted) continue;
          if (e instanceof SoftfiError && e.status === 404) {
            olvidarEnlace();
            continue;
          }
          // Sin internet, o el servidor reiniciando: se reintenta con calma.
          useTelefono.setState({ conectado: false });
          await dormir(3000);
        }
      }
    })();

    return () => {
      vivo = false;
      control.abort();
      soltar();
      window.removeEventListener("focus", marcar);
      window.removeEventListener("storage", alCambiarDePestana);
      document.removeEventListener("visibilitychange", marcar);
    };
  }, []);
}
